import { ampli } from '@/ampli';
import { useQueryClient, type QueryKey } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';

import { useTokenResult } from './tokenQueries/useTokenResult';
import { useActiveToken } from './useActiveToken';
import { useSelectedTokens } from './useSelectedTokens';

type RefreshSource = 'refresh_button' | 'keyboard';

const TRADE_QUOTE_KEY_PREFIXES = new Set([
  'swap-price',
  'swap-quote',
  'gasless-price',
  'gasless-quote',
  'intents-price',
  'intents-quote',
  'solana-swap-quote',
  'cross-chain-quote',
]);

const isTradeQuoteKey = (queryKey: QueryKey): boolean => {
  const head = queryKey[0];
  return typeof head === 'string' && TRADE_QUOTE_KEY_PREFIXES.has(head);
};

/**
 * Returns a stable callback that invalidates (and refetches) every active
 * trade-quote query. Shared by the manual refresh control and the
 * post-cancellation recovery path so the quote key list lives in one place.
 */
export function useInvalidateTradeQuotes() {
  const queryClient = useQueryClient();
  return useCallback(
    () =>
      queryClient.invalidateQueries({
        predicate: (query) => isTradeQuoteKey(query.queryKey),
      }),
    [queryClient],
  );
}

/** Invalidates active quote queries and records the caller's refresh interaction. */
export function useRefreshTrade(sellInputDecimal: string) {
  const invalidateTradeQuotes = useInvalidateTradeQuotes();
  const activeToken = useActiveToken();
  const { sellTokenAddress, sellTokenChainId, buyTokenAddress, buyTokenChainId } =
    useSelectedTokens(activeToken);
  const { token: sellToken } = useTokenResult(sellTokenAddress, sellTokenChainId);
  const { token: buyToken } = useTokenResult(buyTokenAddress, buyTokenChainId);
  const hasActiveQuote = !!sellToken && !!buyToken && Number(sellInputDecimal) > 0;

  const [isRefreshing, setIsRefreshing] = useState(false);
  const inFlightRef = useRef(false);

  const refresh = useCallback(
    async (source: RefreshSource) => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;

      const isCrossChain =
        sellTokenChainId != null && buyTokenChainId != null && sellTokenChainId !== buyTokenChainId;

      if (sellToken && buyToken) {
        ampli.tradeRefreshClicked({
          source,
          trade_type: isCrossChain ? 'cross-chain' : 'market',
          sell_token_address: sellToken.address,
          sell_token_chain_id: sellToken.chainId,
          sell_token_symbol: sellToken.symbol,
          buy_token_address: buyToken.address,
          buy_token_chain_id: buyToken.chainId,
          buy_token_symbol: buyToken.symbol,
        });
      }

      setIsRefreshing(true);
      try {
        await invalidateTradeQuotes();
      } finally {
        inFlightRef.current = false;
        setIsRefreshing(false);
      }
    },
    [sellTokenChainId, buyTokenChainId, sellToken, buyToken, invalidateTradeQuotes],
  );

  return { refresh, isRefreshing, hasActiveQuote };
}
